import type {
  LeaderboardEntrySchema,
  PrizeSchema,
} from "@/types/generated-types";
import { stripFreespinGameName } from "@/utils/helpers";
import {
  selectTournamentById,
  selectTournamentLeaderboardByCampaignId,
} from "@/stores/tournament.store";
import { selectTournamentProgressByCampaignId } from "@/stores/tournament-progress.store";

import {
  getTournamentScoreColumnLabel,
  isHighestWinStyleTournament,
} from "@/utils/tournament-score-label";
import { useAppStore } from "@/stores";
import type { AppStore } from "@/stores/app.store";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

export type TournamentLeaderboardItem = {
  position: number;
  score: number;
  name: string;
  isPlayer: boolean;
  prize?: string;
  /** Stable identity (the player hash) for React keys — so a live score change
   *  that reorders ranks moves the row, not the DOM node under a new player.
   *  Absent for empty placeholder slots. */
  id?: string;
  /** True when no player is at this position (placeholder row to show prize) */
  isEmptySlot?: boolean;
};

const TOP_TEN_COUNT = 10;

function parseScore(value: string | number | undefined): number {
  if (value === undefined || value === null) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const parsed = Number(String(value).replace(/\s/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function positionInRange(
  position: number,
  placeFrom: number | null | undefined,
  placeTo: number | null | undefined
): boolean {
  if (placeFrom != null && position < placeFrom) return false;
  if (placeTo != null && position > placeTo) return false;
  return true;
}

function getPrizeForPosition(
  prizes: PrizeSchema[] | undefined,
  position: number
): string | undefined {
  if (!prizes?.length) return undefined;
  const prize = prizes.find((p) =>
    positionInRange(position, p.place_from, p.place_to)
  );
  const title = prize?.title as string | undefined;
  // The leaderboard cell has no room for a game name: "50 Free Spins in Book of Dead" pushed the
  // player name out, broke the columns and still didn't fit (QA, mobile). The prize list shows
  // the games; here the prize is the count alone.
  return title === undefined ? undefined : stripFreespinGameName(title);
}

function withPrizesMerged(
  items: TournamentLeaderboardItem[],
  prizes: PrizeSchema[] | undefined
): TournamentLeaderboardItem[] {
  return items.map((item) => ({
    ...item,
    prize: item.prize ?? getPrizeForPosition(prizes, item.position),
  }));
}

function mapLeaderboardToItems(
  entries: LeaderboardEntrySchema[] | undefined,
  currentPlayerHash: string | null,
  t: TFunction
): TournamentLeaderboardItem[] {
  if (!entries?.length) return [];
  return entries.map((entry, index) => ({
    position: index + 1,
    score: parseScore(entry.score as string | number | undefined),
    name:
      currentPlayerHash && entry.player === currentPlayerHash
        ? t("tournament.you", "You")
        : entry.player,
    isPlayer: Boolean(currentPlayerHash && entry.player === currentPlayerHash),
    id: entry.player,
    prize: undefined,
  }));
}

/**
 * The board as the panel shows it, from the store alone: the broadcast rows
 * with the player's own live score surfaced ahead of the next broadcast (and
 * the player re-placed above any row they now outscore). Pure, so the in-game
 * launcher's rows — a store selector, not a hook — read the same board.
 */
export function selectLeaderboardItems(
  state: AppStore,
  key: string,
  t: TFunction
): TournamentLeaderboardItem[] {
  const leaderboard = selectTournamentLeaderboardByCampaignId(state, key);
  const progress = selectTournamentProgressByCampaignId(state, key);
  const currentPlayerHash =
    state.playerHash ?? (state.authData?.player_hash as string) ?? null;
  return mergeLiveScore(
    mapLeaderboardToItems(leaderboard, currentPlayerHash, t),
    progress?.score
  );
}

function mergeLiveScore(
  items: TournamentLeaderboardItem[],
  liveScore: number | null | undefined
): TournamentLeaderboardItem[] {
  const playerIndex = items.findIndex((item) => item.isPlayer);
  // Only bump upward: a stale/equal progress score leaves the broadcast untouched, so
  // it fully wins (incl. draw resolution) once it catches up.
  if (
    liveScore == null ||
    playerIndex === -1 ||
    liveScore <= items[playerIndex].score
  ) {
    return items;
  }
  // Surface the player's live score ahead of the next `tournaments` broadcast, and
  // re-place them above any row they now outscore so the board never shows the player
  // behind a lower score. Ties keep their broadcast order (backend draw resolution).
  const others = items.filter((_, index) => index !== playerIndex);
  const targetIndex = others.filter((item) => item.score >= liveScore).length;
  others.splice(targetIndex, 0, { ...items[playerIndex], score: liveScore });
  return others.map((item, index) => ({ ...item, position: index + 1 }));
}

export const useTournamentLeaderboard = (tournamentId?: string) => {
  const selectedId = useAppStore((state) => state.selectedCampaignId);
  const resolvedId = tournamentId ?? selectedId ?? "";

  const leaderboard = useAppStore((state) =>
    resolvedId
      ? selectTournamentLeaderboardByCampaignId(state, resolvedId)
      : undefined
  );
  const tournament = useAppStore((state) =>
    resolvedId ? selectTournamentById(state, resolvedId) : undefined
  );
  const progress = useAppStore((state) =>
    resolvedId
      ? selectTournamentProgressByCampaignId(state, resolvedId)
      : undefined
  );
  const loading = useAppStore(
    (state) => state.tournamentLeaderboardsStream.loading
  );
  const subscribed = useAppStore(
    (state) => state.tournamentLeaderboardsStream.subscribed
  );
  const currentPlayerHash = useAppStore(
    (state) =>
      state.playerHash ?? (state.authData?.player_hash as string) ?? null
  );
  const { t } = useTranslation();

  // The same merge as `selectLeaderboardItems`, on the hook's own subscriptions
  // (so it re-runs only when these slices change).
  const leaderboardItems = useMemo(() => {
    const items = mapLeaderboardToItems(leaderboard, currentPlayerHash, t);
    return mergeLiveScore(items, progress?.score);
  }, [leaderboard, currentPlayerHash, progress, t]);

  const topTenLeaderboardData = useMemo(() => {
    const prizes = tournament?.prizes ?? [];
    return withPrizesMerged(leaderboardItems.slice(0, TOP_TEN_COUNT), prizes);
  }, [leaderboardItems, tournament?.prizes]);

  const fullLeaderboardData = useMemo(() => {
    const prizes = tournament?.prizes ?? [];
    return withPrizesMerged(leaderboardItems, prizes);
  }, [leaderboardItems, tournament?.prizes]);

  // Read the player's row off the prize-merged list so the dashboard's own-player
  // row (shown when the player is outside the top 10) carries its prize too.
  const sortedLeaderboardData = fullLeaderboardData;
  const playerData = sortedLeaderboardData.find((item) => item.isPlayer);
  const isPlayerInTopTen = Boolean(
    playerData && playerData.position <= TOP_TEN_COUNT
  );

  const scoreLabel = getTournamentScoreColumnLabel(tournament?.subtype);

  const showScoreMultiplierSuffix = useMemo(
    () => isHighestWinStyleTournament(tournament?.subtype),
    [tournament]
  );

  // A race caps each player's counted spins: how many of the cap they have left. Races only —
  // every other subtype leaves `left_spins` null.
  const maxSpins = tournament?.campaign.max_spins;
  const raceSpins =
    progress?.left_spins != null && maxSpins
      ? { left: progress.left_spins, total: maxSpins }
      : null;

  // Prize for a given rank, independent of the leaderboard — lets the empty
  // state preview the prize ladder before anyone has joined.
  const prizeForPosition = useMemo(() => {
    const prizes = tournament?.prizes ?? [];
    return (position: number): string | undefined =>
      getPrizeForPosition(prizes, position);
  }, [tournament?.prizes]);

  return {
    leaderboardItems: sortedLeaderboardData,
    leaderboard,
    sortedLeaderboardData,
    fullLeaderboardData,
    topTenLeaderboardData,
    isPlayerInTopTen,
    playerData,
    scoreLabel,
    showScoreMultiplierSuffix,
    raceSpins,
    prizeForPosition,
    subscribed,
    loading,
    /** The tournament has ended: standings are final and a prize is won, not "current". */
    finished: tournament?.campaign.status === "FI",
  };
};
